import { css } from '@emotion/core';
import { IconButton, Theme, Tooltip, useTheme } from '@material-ui/core';
import { OpenInNew as OpenInNewIcon } from '@material-ui/icons';
import * as React from 'react';

import { currencyFormat } from 'shared/common/string-format-utils';
import { AggregateStats, SearchKeywordSets } from 'shared/models/search';

import { useAgent } from '../hooks/use-agent';
import { ChatMarkdown } from './chat/chat-markdown';

const TABLE_ROW_LIMIT = 10;

/* tslint:disable:no-magic-numbers */
const styles = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 20px 10px;

    .strategy-section {
        font-size: 14px;
        line-height: 1.6;
        padding: 0 16px;

        code {
            background: #e9ecef;
            padding: 1px 4px;
            border-radius: 2px;
            font-size: 12px;
        }

        ul,
        ol {
            margin: 8px 0;
            padding-left: 20px;
        }

        li {
            margin: 4px 0;
        }

        p {
            margin: 8px 0;
        }
    }

    .stats-section {
        margin-top: 16px;
        padding: 16px;
        border-top: 1px solid ${theme.palette.divider};
        overflow-x: auto;
    }

    .numeric-stats {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-bottom: 16px;
        font-size: 13px;
    }

    .numeric-stat {
        display: flex;
        gap: 8px;

        .stat-label {
            font-weight: 500;
            min-width: 180px;
        }

        .stat-values {
            color: ${theme.palette.text.secondary};
        }
    }

    .tables-row {
        display: flex;
        gap: 16px;
        margin-bottom: 16px;

        > * {
            flex: 1;
            min-width: 0;
        }
    }

    .stats-table {
        border: 1px solid ${theme.palette.divider};
        border-radius: 4px;
        overflow: hidden;
        font-size: 12px;

        .table-title {
            font-weight: 600;
            font-size: 12px;
            padding: 8px 10px;
            background: ${theme.palette.grey[50]};
            border-bottom: 1px solid ${theme.palette.divider};
        }

        table {
            width: 100%;
            border-collapse: collapse;
        }

        th {
            text-align: left;
            padding: 6px 10px;
            font-weight: 500;
            background: ${theme.palette.grey[100]};
            border-bottom: 1px solid ${theme.palette.divider};
        }

        td {
            padding: 5px 10px;
            border-bottom: 1px solid ${theme.palette.divider};
        }

        tr:last-child td {
            border-bottom: none;
        }

        .count-col,
        .pct-col {
            text-align: right;
            width: 60px;
        }
    }

    .keyword-coverage-table {
        margin-bottom: 0;
    }

    .keyword-terms {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        max-width: 400px;
    }

    .keyword-term {
        display: inline-block;
        border-radius: 3px;
        padding: 1px 6px;
        font-size: 11px;
        line-height: 1.5;
        white-space: nowrap;
        background: #e8f5e9;
        color: #2e7d32;
    }

    .keyword-term--exclude {
        background: #fce4ec;
        color: #c62828;
    }

    .conversation-link {
        display: flex;
        justify-content: flex-end;
        margin-top: 8px;
    }
`;
/* tslint:enable:no-magic-numbers */

interface SearchStrategyDisplayProps {
    strategy: string;
    aggregateStats?: AggregateStats | null;
    keywordSets?: SearchKeywordSets | null;
    conversationId?: string;
}

export const SearchStrategyDisplay: React.FC<SearchStrategyDisplayProps> = ({
    strategy,
    aggregateStats,
    keywordSets,
    conversationId
}) => {
    const theme = useTheme<Theme>();
    const { onOpen: openAgent, onSelectConversation } = useAgent();

    const stats = aggregateStats;
    const totalCount = aggregateStats?.totalCount ?? 0;

    const handleOpenConversation = () => {
        if (conversationId) {
            onSelectConversation(conversationId);
            openAgent();
        }
    };

    const showPct = totalCount > 0;
    const formatPct = (count: number) => (totalCount > 0 ? ((count / totalCount) * 100).toFixed(1) : '-');

    const formatStat = (v: number | null) => (v !== null && !isNaN(v) ? v.toFixed(1) : 'N/A');
    // tslint:disable-next-line:no-magic-numbers
    const formatFunding = (v: number | null) => (v !== null && !isNaN(v) ? currencyFormat(v * 1000000, 0) : 'N/A');

    const renderNumericStats = (s: AggregateStats) => (
        <div className="numeric-stats">
            <div className="numeric-stat">
                <span className="stat-label">Experience (years)</span>
                <span className="stat-values">
                    median: {formatStat(s.experience.median)}, mean: {formatStat(s.experience.mean)}, range:{' '}
                    {formatStat(s.experience.min)}-{formatStat(s.experience.max)}
                </span>
            </div>
            <div className="numeric-stat">
                <span className="stat-label">School Rank (lower=better)</span>
                <span className="stat-values">
                    median: {formatStat(s.schoolRank.median)}, mean: {formatStat(s.schoolRank.mean)}, range:{' '}
                    {formatStat(s.schoolRank.min)}-{formatStat(s.schoolRank.max)}
                </span>
            </div>
            <div className="numeric-stat">
                <span className="stat-label">Company Funding</span>
                <span className="stat-values">
                    median: {formatFunding(s.fundingAmount.median)}, mean: {formatFunding(s.fundingAmount.mean)}, range:{' '}
                    {formatFunding(s.fundingAmount.min)}-{formatFunding(s.fundingAmount.max)}
                </span>
            </div>
        </div>
    );

    const renderCompanyTable = (title: string, data: Array<{ company: string; count: number }>) => {
        if (data.length === 0) return null;
        const rows = data.slice(0, TABLE_ROW_LIMIT).map((row, i) => (
            <tr key={i}>
                <td>{row.company}</td>
                <td className="count-col">{row.count}</td>
                {showPct && <td className="pct-col">{formatPct(row.count)}</td>}
            </tr>
        ));
        return (
            <div className="stats-table">
                <div className="table-title">{title}</div>
                <table>
                    <thead>
                        <tr>
                            <th>Company</th>
                            <th className="count-col">#</th>
                            {showPct && <th className="pct-col">%</th>}
                        </tr>
                    </thead>
                    <tbody>{rows}</tbody>
                </table>
            </div>
        );
    };

    const renderTitleTable = (data: Array<{ title: string; count: number }>) => {
        if (data.length === 0) return null;
        const rows = data.slice(0, TABLE_ROW_LIMIT).map((row, i) => (
            <tr key={i}>
                <td>{row.title}</td>
                <td className="count-col">{row.count}</td>
                {showPct && <td className="pct-col">{formatPct(row.count)}</td>}
            </tr>
        ));
        return (
            <div className="stats-table">
                <div className="table-title">Current Titles</div>
                <table>
                    <thead>
                        <tr>
                            <th>Title</th>
                            <th className="count-col">#</th>
                            {showPct && <th className="pct-col">%</th>}
                        </tr>
                    </thead>
                    <tbody>{rows}</tbody>
                </table>
            </div>
        );
    };

    const renderFundingTable = (data: Array<{ stage: string; count: number }>) => {
        if (data.length === 0) return null;
        const rows = data.map((row, i) => (
            <tr key={i}>
                <td>{row.stage}</td>
                <td className="count-col">{row.count}</td>
                {showPct && <td className="pct-col">{formatPct(row.count)}</td>}
            </tr>
        ));
        return (
            <div className="stats-table">
                <div className="table-title">Funding Stage</div>
                <table>
                    <thead>
                        <tr>
                            <th>Stage</th>
                            <th className="count-col">#</th>
                            {showPct && <th className="pct-col">%</th>}
                        </tr>
                    </thead>
                    <tbody>{rows}</tbody>
                </table>
            </div>
        );
    };

    const renderLocationsTable = (data: Array<{ location: string; count: number }>) => {
        if (data.length === 0) return null;
        const rows = data.slice(0, TABLE_ROW_LIMIT).map((row, i) => (
            <tr key={i}>
                <td>{row.location}</td>
                <td className="count-col">{row.count}</td>
                {showPct && <td className="pct-col">{formatPct(row.count)}</td>}
            </tr>
        ));
        return (
            <div className="stats-table">
                <div className="table-title">Locations</div>
                <table>
                    <thead>
                        <tr>
                            <th>Location</th>
                            <th className="count-col">#</th>
                            {showPct && <th className="pct-col">%</th>}
                        </tr>
                    </thead>
                    <tbody>{rows}</tbody>
                </table>
            </div>
        );
    };

    const renderKeywordCoverageTable = () => {
        const matchRates = stats?.keywordMatchRates;
        if (!matchRates || matchRates.length === 0 || !keywordSets) return null;
        const sorted = [...matchRates].sort((a, b) => {
            if (a.type === b.type) return 0;
            return a.type === 'include' ? -1 : 1;
        });
        const rows = sorted.map((rate, i) => {
            const kw = keywordSets[rate.name];
            const terms = kw?.terms ?? [];
            const isExclude = rate.type === 'exclude';
            const termClass = isExclude ? 'keyword-term keyword-term--exclude' : 'keyword-term';
            const termPills = terms.map((term, j) => (
                <span key={j} className={termClass}>
                    {term}
                </span>
            ));
            return (
                <tr key={i}>
                    <td>
                        <div className="keyword-terms">{termPills}</div>
                    </td>
                    <td className="pct-col">{rate.matchPercent.toFixed(1)}%</td>
                </tr>
            );
        });
        return (
            <div className="stats-table keyword-coverage-table">
                <div className="table-title">
                    Keyword Coverage
                    {stats?.keywordMatchRecencyYears ? ` (last ${stats.keywordMatchRecencyYears}yr)` : ''}
                </div>
                <table>
                    <thead>
                        <tr>
                            <th>Terms</th>
                            <th className="pct-col">Hit %</th>
                        </tr>
                    </thead>
                    <tbody>{rows}</tbody>
                </table>
            </div>
        );
    };

    const keywordCoverageTable = renderKeywordCoverageTable();
    const locationsTable = stats?.locations ? renderLocationsTable(stats.locations) : null;

    const locationsKeywordsRow =
        locationsTable || keywordCoverageTable ? (
            <div className="tables-row">
                {locationsTable}
                {keywordCoverageTable}
            </div>
        ) : null;

    const statsSection = stats ? (
        <div className="stats-section">
            {renderNumericStats(stats)}
            <div className="tables-row">
                {renderCompanyTable('Current Companies', stats.currentCompanies)}
                {renderFundingTable(stats.fundingStage)}
            </div>
            <div className="tables-row">
                {renderTitleTable(stats.currentTitles)}
                {renderCompanyTable('Previous Companies', stats.previousCompanies)}
            </div>
            {locationsKeywordsRow}
        </div>
    ) : null;

    const conversationLink = conversationId ? (
        <div className="conversation-link">
            <Tooltip title="Open in Orbit">
                <IconButton size="small" onClick={handleOpenConversation}>
                    <OpenInNewIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </div>
    ) : null;

    return (
        <div css={styles(theme)}>
            <div className="strategy-section">
                <ChatMarkdown>{strategy}</ChatMarkdown>
            </div>
            {statsSection}
            {conversationLink}
        </div>
    );
};
